import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  dismissToast,
  subscribeToasts,
  type ToastItem,
  type ToastType,
} from "./toastStore";

const META: Record<
  ToastType,
  { bar: string; iconWrap: string; icon: typeof CheckCircle2; progress: string }
> = {
  success: {
    bar: "bg-papaya-leaf",
    iconWrap: "bg-papaya-leaf/15 text-papaya-leaf",
    icon: CheckCircle2,
    progress: "bg-papaya-leaf",
  },
  error: {
    bar: "bg-danger",
    iconWrap: "bg-danger/10 text-danger",
    icon: XCircle,
    progress: "bg-danger",
  },
  warning: {
    bar: "bg-warning",
    iconWrap: "bg-warning/15 text-warning",
    icon: AlertTriangle,
    progress: "bg-warning",
  },
  info: {
    bar: "bg-papaya-deep",
    iconWrap: "bg-papaya/15 text-papaya-deep",
    icon: Info,
    progress: "bg-papaya-deep",
  },
};

function ToastCard({ item }: { item: ToastItem }) {
  const meta = META[item.type];
  const Icon = meta.icon;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto relative flex w-[360px] max-w-[calc(100vw-24px)] animate-[toast-in_0.22s_ease-out] overflow-hidden rounded-[6px] border border-border bg-surface shadow-[0_12px_36px_rgba(65,33,10,0.14)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.45)]"
    >
      <div className={cn("w-[3px] shrink-0", meta.bar)} />
      <div className="flex min-w-0 flex-1 items-start gap-3 px-3.5 py-3">
        <span
          className={cn(
            "mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            meta.iconWrap,
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={2.2} />
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="break-words text-sm font-semibold leading-5 text-fg">{item.title}</p>
          {item.description ? (
            <p className="mt-0.5 break-words text-xs leading-5 text-fg-muted">
              {item.description}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={() => dismissToast(item.id)}
          className="mt-0.5 rounded-[6px] p-1 text-fg-subtle transition hover:bg-surface-muted hover:text-fg"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {item.duration > 0 ? (
        <div className="absolute right-0 bottom-0 left-0 h-[2px] bg-border/60">
          <div
            className={cn("h-full origin-left", meta.progress)}
            style={{ animation: `toast-progress ${item.duration}ms linear forwards` }}
          />
        </div>
      ) : null}
    </div>
  );
}

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => subscribeToasts(setItems), []);

  if (items.length === 0) return null;

  return (
    <div className="pointer-events-none fixed top-5 right-5 z-[9999] flex flex-col items-end gap-2.5">
      {items.map((item) => (
        <ToastCard key={item.id} item={item} />
      ))}
    </div>
  );
}
